import { createSlice } from '@reduxjs/toolkit'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'
import { API } from 'src/utils/interceptor'
import { setFeatureLimit } from './VenueSlice'
import { calculateRecipeAllergens } from 'src/utils/recipeAllergenCalculator'
import Sentry from 'src/utils/sentry'
import {
  GROWTH_ENGINE_GUIDED_LANE_REASONS,
  emitGrowthEngineGuidedLaneProgress,
} from 'src/utils/growthEngineNudgeRefresh'
import {
  assertCustomIngredientSaveResponse,
  isCustomIngredientSaveError,
  logCustomIngredientSaveFailure,
  notifyCustomIngredientSaveError,
} from 'src/utils/customIngredientSaveErrors'
import {
  readRecipeAllowance,
  recipeUnmonitoredSaveNotice,
} from 'src/utils/recipeMonitoring'
import { isOverRecipeMonitorCap } from 'src/constants/recipePlanAllowances'

const initialState = {
  recipes: [],
  recipeAllowance: null,
  monitorCapUpgrade: false,
  categories: [],
  isLoading: false,
  error: null,
  success: false,
  products: [],
}

const matchesVenueProductId = (ingredient, venueProductId) => {
  const targetId = String(venueProductId || '')
  if (!targetId) return false
  const product = ingredient?.product
  if (typeof product === 'string' || typeof product === 'number') {
    return String(product) === targetId
  }
  return (
    String(product?.venueProductId || '') === targetId ||
    String(ingredient?.venueProductId || '') === targetId ||
    String(ingredient?.productId || '') === targetId
  )
}

const getIngredientProductKey = ingredient => {
  const product = ingredient?.product
  if (typeof product === 'string' || typeof product === 'number') {
    return String(product)
  }
  return String(
    product?.venueProductId ||
      ingredient?.venueProductId ||
      ingredient?.productId ||
      product?._id ||
      '',
  )
}

const isLeanProductRef = product =>
  !product || typeof product === 'string' || typeof product === 'number'

const isLeanConversionRef = conversion => {
  if (conversion == null || conversion === '') return false
  if (typeof conversion === 'string' || typeof conversion === 'number') {
    return true
  }
  if (typeof conversion !== 'object') return true
  return conversion.toUnit == null && conversion.toQty == null
}

const getCustomIngredientProductKey = entry => {
  const product = entry?.product
  if (typeof product === 'string' || typeof product === 'number') {
    return String(product)
  }
  return String(product?._id || '')
}

/**
 * Edit responses often return customIngredients.product as bare IDs. Prefer
 * populated refs from the list recipe so detail/edit views keep title/sku/price.
 */
export const mergeCustomIngredientsPreservingPopulatedRefs = (
  existingCustomIngredients = [],
  updatedCustomIngredients,
) => {
  if (!Array.isArray(updatedCustomIngredients)) {
    return existingCustomIngredients || []
  }
  if (updatedCustomIngredients.length === 0) {
    return updatedCustomIngredients
  }
  if (
    !Array.isArray(existingCustomIngredients) ||
    existingCustomIngredients.length === 0
  ) {
    return updatedCustomIngredients
  }

  return updatedCustomIngredients.map((updated, index) => {
    const updatedProductKey = getCustomIngredientProductKey(updated)
    const existing =
      (updatedProductKey &&
        existingCustomIngredients.find(
          item => getCustomIngredientProductKey(item) === updatedProductKey,
        )) ||
      (updated?._id &&
        existingCustomIngredients.find(
          item => String(item._id) === String(updated._id),
        )) ||
      existingCustomIngredients[index]

    if (!existing) return updated

    return {
      ...existing,
      ...updated,
      product: isLeanProductRef(updated.product)
        ? existing.product || updated.product
        : updated.product,
      title: updated.title || existing.title,
      sku: updated.sku || existing.sku,
      UoM: updated.UoM || existing.UoM,
    }
  })
}

/**
 * Edit/save responses often return product/conversion as bare IDs. Prefer the
 * already-populated list-recipe refs so re-opening Edit does not crash.
 */
export const mergeIngredientsPreservingPopulatedRefs = (
  existingIngredients = [],
  updatedIngredients,
) => {
  if (!Array.isArray(updatedIngredients)) {
    return existingIngredients || []
  }
  if (updatedIngredients.length === 0) {
    return updatedIngredients
  }
  if (!Array.isArray(existingIngredients) || existingIngredients.length === 0) {
    return updatedIngredients
  }

  return updatedIngredients.map((updated, index) => {
    const updatedKey = getIngredientProductKey(updated)
    const existing =
      (updatedKey &&
        existingIngredients.find(
          item => getIngredientProductKey(item) === updatedKey,
        )) ||
      existingIngredients[index]

    if (!existing) return updated

    return {
      ...existing,
      ...updated,
      product: isLeanProductRef(updated.product)
        ? existing.product || updated.product
        : updated.product,
      conversion: isLeanConversionRef(updated.conversion)
        ? existing.conversion || updated.conversion
        : updated.conversion,
      conversions:
        Array.isArray(updated.conversions) && updated.conversions.length > 0
          ? updated.conversions
          : existing.conversions || updated.conversions || [],
      title: updated.title || existing.title,
      sku: updated.sku || existing.sku,
    }
  })
}

const recipeBuilderSlice = createSlice({
  name: 'recipes',
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setRecipes: (state, action) => {
      state.recipes = action.payload
    },
    setRecipeAllowance: (state, action) => {
      state.recipeAllowance = action.payload
    },
    setMonitorCapUpgrade: (state, action) => {
      state.monitorCapUpgrade = Boolean(action.payload)
    },
    upsertRecipe: (state, action) => {
      const recipe = action.payload
      if (!recipe?._id) return
      const index = state.recipes.findIndex(
        item => String(item._id) === String(recipe._id),
      )
      if (index >= 0) {
        state.recipes[index] = {
          ...state.recipes[index],
          ...recipe,
        }
      } else {
        state.recipes.push(recipe)
      }
    },
    setCategories: (state, action) => {
      state.categories = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    setSuccess: (state, action) => {
      state.success = action.payload
    },
    updateProduct: (state, action) => {
      const updatedProduct = action.payload
      state.products = state.products.map(prod =>
        prod._id === updatedProduct._id ? updatedProduct : prod,
      )
    },
    setProducts: (state, action) => {
      state.products = action.payload.reduce((accumulator, currentItem) => {
        if (!accumulator.some(item => item._id === currentItem._id)) {
          accumulator.push(currentItem)
        }
        return accumulator
      }, [])
    },
  },
})

export const fetchAllRecipes = venueId => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const data = await API.get('api2', `recipes/venue/${venueId}`)
    dispatch(setRecipes(data?.recipes))
    dispatch(setRecipeAllowance(readRecipeAllowance(data)))
    dispatch(setIsLoading(false))
  } catch (error) {
    dispatch(setError(error))
    dispatch(setIsLoading(false))
    Sentry.captureException(error)
  }
}

export const fetchCategories = venueId => async dispatch => {
  try {
    const data = await API.get(
      'api2',
      `recipes/categories/${venueId}/fetchCategories`,
    )
    dispatch(setCategories(data.categories))
  } catch (error) {
    dispatch(setError(error))
    Sentry.captureException(error)
  }
}

export const createCategory =
  (venueId, categoryName) => async (dispatch, getState) => {
    try {
      const response = await API.post(
        'api2',
        `recipes/categories/${venueId}/createCategory`,
        {
          body: { categoryName },
        },
      )
      const createdCategory = response?.category
      if (createdCategory) {
        const currentCategories = getState()?.recipes?.categories || []
        dispatch(setCategories([...currentCategories, createdCategory]))
      }
      return createdCategory
    } catch (error) {
      dispatch(setError(error))
      Sentry.captureException(error)
      throw error
    }
  }

export const getProducts = venueId => async dispatch => {
  try {
    const products = await API.get('api', `venue/${venueId}/products`)
    dispatch(setProducts(products))
  } catch (error) {
    dispatch(setError(error))
    Sentry.captureException(error)
  }
}

export const toggleRecipePin =
  (recipeId, venueId, pinned) => async (dispatch, getState) => {
    const state = getState()
    const currentRecipes = state?.recipes?.recipes || []

    const optimisticRecipes = currentRecipes.map(recipe =>
      recipe._id === recipeId
        ? {
            ...recipe,
            pinned,
            pinnedAt: pinned
              ? recipe?.pinnedAt || new Date().toISOString()
              : undefined,
          }
        : recipe,
    )

    dispatch(setRecipes(optimisticRecipes))

    try {
      await API.patch('api2', `recipes/${recipeId}/pin`, {
        body: { pinned, venueId },
      })
    } catch (error) {
      dispatch(setRecipes(currentRecipes))
      dispatch(setError(error))
      Sentry.captureException(error)
      throw error
    }
  }

export const repairStaleEconomics =
  (venueId, recipeIds) => async (dispatch, getState) => {
    const normalizedIds = [
      ...new Set(
        (Array.isArray(recipeIds) ? recipeIds : [])
          .map(id => String(id || '').trim())
          .filter(Boolean),
      ),
    ]

    if (!venueId || !normalizedIds.length) {
      return { applied: 0, results: [] }
    }

    try {
      const response = await API.post(
        'api2',
        `recipes/venue/${venueId}/repair-stale-economics`,
        { body: { recipeIds: normalizedIds } },
      )

      const results = response?.results || []
      const existingRecipes = getState()?.recipes?.recipes || []

      results.forEach(result => {
        const existingRecipe = existingRecipes.find(
          recipe => String(recipe._id) === String(result.recipeId),
        )

        const mergedRecipe = {
          ...(existingRecipe || {}),
          _id: result.recipeId,
          costPerRecipe: result.costPerRecipe,
          costPerPortion: result.costPerPortion,
          grossProfit: result.grossProfit,
          needsReview: result.needsReview,
        }

        if (result.gpChanged && existingRecipe) {
          mergedRecipe.previousGrossProfit =
            existingRecipe.previousGrossProfit ??
            (existingRecipe.grossProfit != null
              ? String(existingRecipe.grossProfit)
              : null)
          mergedRecipe.previousCostPerRecipe =
            existingRecipe.previousCostPerRecipe ??
            existingRecipe.costPerRecipe ??
            null
          mergedRecipe.gpChangedAt =
            existingRecipe.gpChangedAt ?? new Date().toISOString()
        }

        dispatch(upsertRecipe(mergedRecipe))
      })

      return response
    } catch (error) {
      Sentry.captureException(error)
      throw error
    }
  }

export const markRecipeReviewed =
  (recipeId, venueId) => async (dispatch, getState) => {
    const state = getState()
    const currentRecipes = state?.recipes?.recipes || []
    const nextRecipes = currentRecipes.map(recipe =>
      recipe._id === recipeId
        ? {
            ...recipe,
            needsReview: false,
            previousGrossProfit: null,
            previousCostPerRecipe: null,
            gpChangedAt: null,
          }
        : recipe,
    )

    dispatch(setRecipes(nextRecipes))

    try {
      await API.patch('api2', `recipes/${recipeId}/review`, {
        body: { venueId },
      })
    } catch (error) {
      dispatch(setRecipes(currentRecipes))
      dispatch(setError(error))
      Sentry.captureException(error)
      throw error
    }
  }

export const deleteRecipe = (recipedId, venueId) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const result = await API.del('api2', `recipes/${recipedId}`)
    SuccessNotification({ message: result?.message })
    await dispatch(fetchAllRecipes(venueId))
  } catch (error) {
    dispatch(setError(error))
    Sentry.captureException(error)
  }
}

export const createRecipe = (venueId, formData) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const data = { ...formData, venue: venueId }
    const response = await API.post('api2', 'recipes/createRecipe', {
      body: data,
    })
    assertCustomIngredientSaveResponse(response?.customIngredientSave)
    const unmonitoredNotice = recipeUnmonitoredSaveNotice(response)
    SuccessNotification(unmonitoredNotice || { message: response?.message })
    const allowance = readRecipeAllowance(response)
    if (allowance) dispatch(setRecipeAllowance(allowance))
    dispatch(
      setMonitorCapUpgrade(
        response?.monitored === false && !isOverRecipeMonitorCap(response),
      ),
    )
    dispatch(setIsLoading(false))
    dispatch(setSuccess(true))
    dispatch(
      setFeatureLimit({ feature: 'Menu_Builder', limit: response.limit }),
    )
    await dispatch(fetchAllRecipes(venueId))
    await dispatch(fetchCategories(venueId))
    if (venueId) {
      emitGrowthEngineGuidedLaneProgress(
        venueId,
        GROWTH_ENGINE_GUIDED_LANE_REASONS.RECIPE_SAVED,
      )
    }
  } catch (error) {
    dispatch(setIsLoading(false))
    const notificationPayload = isCustomIngredientSaveError(error)
      ? notifyCustomIngredientSaveError(error)
      : {
          message:
            error?.response?.data?.message ||
            error?.data?.message ||
            (Array.isArray(error?.errors) && error?.errors?.[0]?.message) ||
            error?.message ||
            'Failed to create recipe',
        }

    if (isCustomIngredientSaveError(error)) {
      logCustomIngredientSaveFailure('createRecipe', error)
    }

    dispatch(setError(error))
    dispatch(setSuccess(false))
    RejectedNotification(notificationPayload)
    Sentry.captureException(error)
  }
}
export const archiveRecipe = (recipeId, venueId) => async dispatch => {
  try {
    const response = await API.post('api2', `recipes/${recipeId}/archive`)
    dispatch(upsertRecipe({ _id: recipeId, monitored: false }))
    await dispatch(fetchAllRecipes(venueId))
    return response
  } catch (error) {
    dispatch(setError(error))
    Sentry.captureException(error)
    throw error
  }
}

export const restoreRecipe = (recipeId, venueId) => async dispatch => {
  try {
    const response = await API.post('api2', `recipes/${recipeId}/restore`)
    if (response?.monitored === true) {
      dispatch(upsertRecipe({ _id: recipeId, monitored: true }))
      await dispatch(fetchAllRecipes(venueId))
    }
    return response
  } catch (error) {
    dispatch(setError(error))
    Sentry.captureException(error)
    throw error
  }
}

export const duplicateRecipe = (venueId, recipeId) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.post(
      'api2',
      `recipes/${recipeId}/duplicate?venueId=${venueId}`,
    )
    const unmonitoredNotice = recipeUnmonitoredSaveNotice(response)
    SuccessNotification(unmonitoredNotice || { message: response?.message })
    const allowance = readRecipeAllowance(response)
    if (allowance) dispatch(setRecipeAllowance(allowance))
    dispatch(
      setMonitorCapUpgrade(
        response?.monitored === false && !isOverRecipeMonitorCap(response),
      ),
    )
    dispatch(setIsLoading(false))
    dispatch(setSuccess(true))
    await dispatch(fetchAllRecipes(venueId))
  } catch (error) {
    dispatch(setIsLoading(false))
    dispatch(setError(error))
    Sentry.captureException(error)
  }
}

export const syncRecipesAfterIngredientUpdate =
  updatedProduct => (dispatch, getState) => {
    if (!updatedProduct?._id) return

    const state = getState()
    const recipes = state?.recipes?.recipes || []
    const venueProducts = state?.venueProducts?.products || []
    const nextVenueProducts = venueProducts.map(product =>
      String(product._id) === String(updatedProduct._id)
        ? { ...product, ...updatedProduct }
        : product,
    )

    // Keep product list in recipes slice in sync too (used by recipe forms).
    dispatch(updateProduct(updatedProduct))

    recipes.forEach(recipe => {
      const usesUpdatedProduct = (recipe?.ingredients || []).some(ingredient =>
        matchesVenueProductId(ingredient, updatedProduct._id),
      )
      if (!usesUpdatedProduct) return

      const nextIngredients = (recipe.ingredients || []).map(ingredient => {
        if (!matchesVenueProductId(ingredient, updatedProduct._id)) {
          return ingredient
        }

        const existingProduct = ingredient?.product || {}
        return {
          ...ingredient,
          title: updatedProduct.title || ingredient.title,
          product: {
            ...existingProduct,
            ...updatedProduct.product,
            venueProductId: updatedProduct._id,
            title: updatedProduct.title || existingProduct.title,
            conversions:
              updatedProduct.conversions || existingProduct.conversions || [],
            allergens:
              updatedProduct.allergens || existingProduct.allergens || [],
            allergenTraces:
              updatedProduct.allergenTraces ||
              existingProduct.allergenTraces ||
              [],
          },
          conversions:
            updatedProduct.conversions || ingredient.conversions || [],
          allergens: updatedProduct.allergens || ingredient.allergens || [],
          allergenTraces:
            updatedProduct.allergenTraces || ingredient.allergenTraces || [],
        }
      })

      const recipeWithUpdatedIngredients = {
        ...recipe,
        ingredients: nextIngredients,
      }
      const allergenCalc = calculateRecipeAllergens(
        recipeWithUpdatedIngredients,
        nextVenueProducts,
        recipes,
      )

      dispatch(
        upsertRecipe({
          ...recipeWithUpdatedIngredients,
          allergens: allergenCalc.allergens,
          allergenTraces: allergenCalc.allergenTraces,
        }),
      )
    })
  }

export const editRecipe =
  (recipeId, updatedData, venueId) => async (dispatch, getState) => {
    const payload = {
      ...updatedData,
      venueId,
    }
    dispatch(setIsLoading(true))
    try {
      const data = await API.put('api2', `recipes/${recipeId}`, {
        body: payload,
      })

      assertCustomIngredientSaveResponse(data?.customIngredientSave)
      SuccessNotification({ message: data?.message })
      dispatch(setIsLoading(false))
      dispatch(setSuccess(true))

      const existingRecipes = getState()?.recipes?.recipes || []
      const existingRecipe = existingRecipes.find(
        recipe => String(recipe._id) === String(recipeId),
      )
      const updatedRecipe = data?.data

      if (updatedRecipe?._id || existingRecipe) {
        // Merge so we keep rich list fields (menuInfo/allergens/etc.) that
        // the edit endpoint does not return in the same shape as getAllRecipes.
        // Also preserve populated product/conversion objects when the edit
        // response returns bare IDs (re-open Edit would otherwise crash).
        dispatch(
          upsertRecipe({
            ...(existingRecipe || {}),
            ...(updatedRecipe || {}),
            _id: recipeId,
            ingredients: mergeIngredientsPreservingPopulatedRefs(
              existingRecipe?.ingredients,
              updatedRecipe?.ingredients ?? existingRecipe?.ingredients,
            ),
            customIngredients: mergeCustomIngredientsPreservingPopulatedRefs(
              existingRecipe?.customIngredients,
              updatedRecipe?.customIngredients ??
                existingRecipe?.customIngredients,
            ),
            menuInfo: existingRecipe?.menuInfo || updatedRecipe?.menuInfo,
            sectionIds: existingRecipe?.sectionIds || updatedRecipe?.sectionIds,
            allergens: existingRecipe?.allergens || updatedRecipe?.allergens,
            allergenTraces:
              existingRecipe?.allergenTraces || updatedRecipe?.allergenTraces,
          }),
        )
      }

      // Linked recipes may have changed server-side; only then do a full refresh.
      if (Number(data?.linkedRecipesUpdated) > 0) {
        await dispatch(fetchAllRecipes(venueId))
      }
      return true
    } catch (error) {
      const notificationPayload = isCustomIngredientSaveError(error)
        ? notifyCustomIngredientSaveError(error)
        : {
            message:
              error?.response?.data?.message ||
              error?.data?.message ||
              (Array.isArray(error?.errors) && error?.errors?.[0]?.message) ||
              error?.message ||
              'Failed to edit recipe',
          }

      if (isCustomIngredientSaveError(error)) {
        logCustomIngredientSaveFailure('editRecipe', error)
      }

      dispatch(setError(error))
      dispatch(setSuccess(false))
      RejectedNotification(notificationPayload)
      Sentry.captureException(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const editIngredient = data => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.put('api2', `recipes/editIngredient`, {
      body: data,
    })

    const updatedProduct = response?.updatedVenueProduct

    if (updatedProduct) {
      // Update product + only recipes that use this product (no full list refetch).
      dispatch(syncRecipesAfterIngredientUpdate(updatedProduct))
      SuccessNotification({
        message: response?.message || 'Ingredient updated successfully',
      })

      return updatedProduct
    } else {
      throw new Error('No updated product returned from server')
    }
  } catch (error) {
    console.error('Error in editIngredient action:', error)
    dispatch(setError(error))
    Sentry.captureException(error)

    // Show error notification
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to update ingredient'
    RejectedNotification({ message: errorMessage })

    throw error // Re-throw so the component can handle it
  } finally {
    dispatch(setIsLoading(false))
  }
}

export const checkIngredients = data => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.post('api2', `recipes/checkIngredients`, {
      body: data,
    })

    return response
  } catch (error) {
    dispatch(setError(error))
    Sentry.captureException(error)
    const errorMessage =
      error?.response?.data?.message ||
      error?.data?.message ||
      error?.message ||
      'Failed to check ingredients'
    return { success: false, message: errorMessage }
  } finally {
    dispatch(setIsLoading(false))
  }
}

export const shareRecipe =
  (recipedId, venueIds, { forceShare = false } = {}) =>
  async dispatch => {
    dispatch(setIsLoading(true))
    try {
      const response = await API.post(
        'api2',
        `recipes/${recipedId}/shareRecipe`,
        {
          body: {
            venueIds,
            forceShare,
          },
        },
      )

      if (response.success) {
        SuccessNotification({ message: response?.message })
      } else {
        RejectedNotification({
          message: response?.message || 'Failed to share recipe',
        })
      }
      return response
    } catch (error) {
      dispatch(setError(error))
      Sentry.captureException(error)
      const errorMessage =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        'Failed to share recipe'
      RejectedNotification({ message: errorMessage })
      return { success: false, message: errorMessage }
    } finally {
      dispatch(setIsLoading(false))
    }
  }

// @desc Update custom ingredient allergens
// @route PATCH /api/v1/recipes/:recipeId/custom-ingredients/:ingredientId/allergens
export const updateCustomIngredientAllergens =
  (recipeId, ingredientId, allergenData) => async dispatch => {
    dispatch(setIsLoading(true))
    try {
      const response = await API.patch(
        'api2',
        `recipes/${recipeId}/custom-ingredients/${ingredientId}/allergens`,
        {
          body: allergenData,
        },
      )

      if (response.success) {
        SuccessNotification({
          message:
            response?.message ||
            'Custom ingredient allergens updated successfully',
        })

        // Refresh recipes to ensure allergen data is immediately updated
        const venueId = allergenData.venueId
        if (venueId) {
          dispatch(fetchAllRecipes(venueId))
        }

        return response
      } else {
        throw new Error(
          response?.message || 'Failed to update custom ingredient allergens',
        )
      }
    } catch (error) {
      console.error('Error in updateCustomIngredientAllergens action:', error)
      dispatch(setError(error))
      Sentry.captureException(error)

      // Show error notification
      const errorMessage =
        error?.message || 'Error updating custom ingredient allergens'
      RejectedNotification({ message: errorMessage })
      throw error
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const {
  setIsLoading,
  setRecipes,
  setRecipeAllowance,
  setMonitorCapUpgrade,
  upsertRecipe,
  setError,
  setCategories,
  setSuccess,
  setProducts,
  updateProduct,
} = recipeBuilderSlice.actions

export default recipeBuilderSlice
